// App.js - Corrected with proper imports and all routes
import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate} from 'react-router-dom';
import { collection, onSnapshot, query, orderBy, limit, getDocs } from 'firebase/firestore';
import { AuthProvider } from './contexts/AuthContext';
import { ToastProvider } from './contexts/ToastContext';
import { WalletProvider } from './contexts/WalletContext';
import Layout from './components/Layout/Layout';
import Toast from './components/Common/Toast';
import PrivateRoute from './components/Common/PrivateRoute';
import AddYearModal from './components/Modals/AddYearModal';
import NotificationModal from './components/Modals/NotificationModal';
import LoadingSpinner from './components/Common/LoadingSpinner';
import AuthGate from './components/AuthGate';


// Import pages
import Landing from './pages/Landing';
import Dashboard from './pages/Dashboard';
import Login from './pages/Login';
import Register from './pages/Register';
import Members from './pages/Members';
import Family from './pages/Family';
import FamilyAllocation from './pages/Family'; // Or create a separate FamilyAllocation.jsx
import Missions from './pages/Missions';
import Events from './pages/Events';
import Council from './pages/Council';
import Gallery from './pages/Gallery';
import Payment from './pages/Payment';
import Settings from './pages/Settings';
import Music from './pages/Music';
import Welfare from './pages/Welfare';
import Documents from './pages/Documents';
import Projects from './pages/Projects';
import Uniform from './pages/Uniform';
import Finance from './pages/Finance';
import HelpMe from './pages/HelpMe';
import ForgotPassword from './pages/ForgotPassword';
import Concert from './pages/ConcertTicket';
import Prayers from './pages/Prayers.jsx';
import Communication from './pages/Communication';
import SMSCommunication from './pages/SMSCommunication';
import MissionToolkit from './pages/MissionToolkit';
import Letter from './pages/Letter';
import UserWallet from './pages/UserWallet';
import AdminWallet from './components/AdminWallet';
import SuperAdmin from './pages/SuperAdmin';
import Hymns from './pages/Hymns';
// Import Firebase
import { db } from './firebase/config';
import './App.css';

// Email notification service
const emailService = {
    sendEmail: async (to, subject, template, data) => {
        try {
            console.log(`Sending ${template} email to:`, to);
            
            const response = await fetch('/.netlify/functions/send-email', {
                method: 'POST',
                headers: { 
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ 
                    to, 
                    subject, 
                    template, 
                    data 
                })
            });
            
            const result = await response.json();
            
            if (!result.success) {
                console.error('Email sending failed:', result.error);
            } else {
                console.log('Email sent successfully:', result.messageId);
            }
            
            return result;
        } catch (error) {
            console.error('Error sending email:', error);
            return { success: false, error: error.message };
        }
    },

    sendBulkEmails: async (recipients, subject, template, data) => {
        if (!recipients || recipients.length === 0) {
            console.log('No recipients to send emails to');
            return [];
        }
        const promises = recipients.map(email => 
            emailService.sendEmail(email, subject, template, data)
        );
        return await Promise.allSettled(promises);
    }
};

function App() {
    const [addYearModalOpen, setAddYearModalOpen] = useState(false);
    const [notificationModalOpen, setNotificationModalOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    const [adminEmails, setAdminEmails] = useState([]);
    const [userEmails, setUserEmails] = useState([]);
    const [firebaseReady, setFirebaseReady] = useState(false);

    // Check if Firebase is initialized
    useEffect(() => {
        const checkFirebase = () => {
            console.log('Checking Firebase db in App:', db);
            
            if (db) {
                console.log('Firebase db is ready');
                setFirebaseReady(true);
                setLoading(false);
            } else {
                console.error('Firebase db is still undefined, retrying...');
                setTimeout(checkFirebase, 1000);
            }
        };
        
        checkFirebase();
    }, []);

    // Load emails and initialize listeners when Firebase is ready
    useEffect(() => {
        if (!firebaseReady) {
            console.log('Firebase not ready, waiting...');
            return;
        }

        console.log('Firebase ready, initializing app...');

        // Listen for modal open events
        const handleOpenModal = (e) => {
            const { modalId } = e.detail;
            if (modalId === 'addYearModal') {
                setAddYearModalOpen(true);
            } else if (modalId === 'notificationModal') {
                setNotificationModalOpen(true);
            }
        };

        window.addEventListener('openModal', handleOpenModal);
        
        // Load all emails
        loadAllEmails();
        
        // Initialize notification listeners
        initializeNotificationListeners();

        return () => {
            window.removeEventListener('openModal', handleOpenModal);
        };
    }, [firebaseReady]);

    // Load all user and admin emails
    const loadAllEmails = async () => {
        try {
            if (!firebaseReady || !db) {
                console.log('Firebase not ready, skipping email load');
                setAdminEmails(['info.whitedovesministry@gmail.com']);
                return;
            }

            console.log('Loading admin emails...');
            
            const adminsSnapshot = await getDocs(collection(db, 'admins'));
            const admins = adminsSnapshot.docs
                .map(doc => doc.data().email)
                .filter(email => email && email.includes('@'));
            setAdminEmails(admins);
            console.log('Admin emails loaded:', admins.length);

            const usersSnapshot = await getDocs(collection(db, 'users'));
            const users = usersSnapshot.docs
                .map(doc => doc.data().email)
                .filter(email => email && email.includes('@'));
            setUserEmails(users);
            console.log('User emails loaded:', users.length);

        } catch (error) {
            console.error('Error loading emails:', error);
            setAdminEmails(['info.whitedovesministry@gmail.com']);
        }
    };

    // Handle user registration success
    const handleUserRegistration = async (userData) => {
        console.log('New user registered:', userData.email);
        
        try {
            await emailService.sendEmail(
                userData.email,
                'Welcome to White Doves Ministry!',
                'userWelcome',
                {
                    name: userData.name,
                    email: userData.email,
                    phone: userData.phone,
                    yearName: userData.yearName
                }
            );
            
            if (adminEmails.length > 0) {
                await emailService.sendBulkEmails(
                    adminEmails,
                    'New Member Joined',
                    'adminNewUserNotification',
                    {
                        name: userData.name,
                        email: userData.email,
                        phone: userData.phone,
                        yearName: userData.yearName
                    }
                );
            }

            console.log('Registration emails sent successfully');
        } catch (error) {
            console.error('Error sending registration emails:', error);
        }
    };

    // Initialize all notification listeners
    const initializeNotificationListeners = () => {
        try {
            if (!firebaseReady || !db) {
                console.log('Firebase not ready, skipping listeners');
                return;
            }

            console.log('Initializing notification listeners...');

            const projectsQuery = query(
                collection(db, 'projects'),
                orderBy('createdAt', 'desc'),
                limit(1)
            );
            
            onSnapshot(projectsQuery, (snapshot) => {
                snapshot.docChanges().forEach((change) => {
                    if (change.type === 'added' && !change.doc.metadata.hasPendingWrites) {
                        const project = { id: change.doc.id, ...change.doc.data() };
                        console.log('New project detected:', project.title);
                        notifyAllUsers('project', project);
                    }
                });
            }, (error) => {
                console.error('Error listening to projects:', error);
            });

            const missionsQuery = query(
                collection(db, 'missions'),
                orderBy('createdAt', 'desc'),
                limit(1)
            );
            
            onSnapshot(missionsQuery, (snapshot) => {
                snapshot.docChanges().forEach((change) => {
                    if (change.type === 'added' && !change.doc.metadata.hasPendingWrites) {
                        const mission = { id: change.doc.id, ...change.doc.data() };
                        console.log('New mission detected:', mission.title);
                        notifyAllUsers('mission', mission);
                    }
                });
            }, (error) => {
                console.error('Error listening to missions:', error);
            });

            const galleryQuery = query(
                collection(db, 'gallery-albums'),
                orderBy('createdAt', 'desc'),
                limit(1)
            );
            
            onSnapshot(galleryQuery, (snapshot) => {
                snapshot.docChanges().forEach((change) => {
                    if (change.type === 'added' && !change.doc.metadata.hasPendingWrites) {
                        const album = { id: change.doc.id, ...change.doc.data() };
                        console.log('New gallery album detected:', album.title);
                        notifyAllUsers('gallery', album);
                    }
                });
            }, (error) => {
                console.error('Error listening to gallery:', error);
            });

            const songsQuery = query(
                collection(db, 'songs'),
                orderBy('addedAt', 'desc'),
                limit(1)
            );
            
            onSnapshot(songsQuery, (snapshot) => {
                snapshot.docChanges().forEach((change) => {
                    if (change.type === 'added' && !change.doc.metadata.hasPendingWrites) {
                        const song = { id: change.doc.id, ...change.doc.data() };
                        console.log('New song detected:', song.title);
                        notifyAllUsers('song', song);
                    }
                });
            }, (error) => {
                console.error('Error listening to songs:', error);
            });

        } catch (error) {
            console.error('Error initializing notification listeners:', error);
        }
    };

    // Notify ALL users
    const notifyAllUsers = async (type, data) => {
        const allEmails = [...new Set([...adminEmails, ...userEmails])];
        
        if (allEmails.length === 0) {
            console.log('No users to notify');
            return;
        }
        
        let subject, template, emailData;
        
        switch(type) {
            case 'project':
                subject = '🚀 New Ministry Project Launched!';
                template = 'newProject';
                emailData = {
                    title: data.title,
                    description: data.description?.substring(0, 100) + '...',
                    location: data.location,
                    targetAmount: data.targetAmount,
                    progress: Math.round((data.currentAmount / data.targetAmount) * 100) || 0
                };
                break;

            case 'mission':
                subject = '🌍 New Mission Opportunity!';
                template = 'newMission';
                emailData = {
                    title: data.title,
                    description: data.description?.substring(0, 100) + '...',
                    location: data.location,
                    leader: data.leader,
                    duration: data.startDate && data.endDate ? 
                        `${new Date(data.startDate).toLocaleDateString()} - ${new Date(data.endDate).toLocaleDateString()}` : 
                        'To be announced'
                };
                break;

            case 'gallery':
                subject = '📸 New Photos Added!';
                template = 'newGallery';
                emailData = {
                    title: data.title,
                    photoCount: data.photoCount,
                    date: data.date
                };
                break;

            case 'song':
                subject = '🎵 New Song Available!';
                template = 'newSong';
                emailData = {
                    title: data.title,
                    artist: data.artist,
                    platform: data.platform
                };
                break;

            default:
                return;
        }

        console.log(`Notifying ${allEmails.length} users about new ${type}`);
        await emailService.sendBulkEmails(allEmails, subject, template, emailData);
    };

    if (loading) {
        return <LoadingSpinner text="Loading White Doves Ministry..." />;
    }

    return (
        <Router>
            <AuthProvider>
                <ToastProvider>
                    <WalletProvider>
                        <Layout>
                            <AuthGate>
                            <Routes>
                                {/* Public Routes */}
                                <Route path="/" element={<Landing />} />
                                <Route path="/login" element={<Login />} />
                                <Route path="/forgot-password" element={<ForgotPassword />} />
                                <Route 
                                    path="/register" 
                                    element={
                                        <Register onRegisterSuccess={handleUserRegistration} />
                                    } 
                                />

                                {/* Protected Routes - User Access */}
                                <Route path="/dashboard" element={<PrivateRoute><Dashboard /></PrivateRoute>} />
                                <Route path="/members" element={<PrivateRoute><Members /></PrivateRoute>} />
                                <Route path="/missions" element={<PrivateRoute><Missions /></PrivateRoute>} />
                                <Route path="/events" element={<PrivateRoute><Events /></PrivateRoute>} />
                                <Route path="/council" element={<PrivateRoute><Council /></PrivateRoute>} />
                                <Route path="/gallery" element={<PrivateRoute><Gallery /></PrivateRoute>} />
                                <Route path="/payment" element={<PrivateRoute><Payment /></PrivateRoute>} />
                                <Route path="/settings" element={<PrivateRoute><Settings /></PrivateRoute>} />
                                <Route path="/music" element={<PrivateRoute><Music /></PrivateRoute>} />
                                <Route path="/welfare" element={<PrivateRoute><Welfare /></PrivateRoute>} />
                                <Route path="/documents" element={<PrivateRoute><Documents /></PrivateRoute>} />
                                <Route path="/projects" element={<PrivateRoute><Projects /></PrivateRoute>} />
                                <Route path="/uniforms" element={<PrivateRoute><Uniform /></PrivateRoute>} />
                                <Route path="/help-me" element={<PrivateRoute><HelpMe /></PrivateRoute>} />
                                <Route path="/prayers" element={<PrivateRoute><Prayers /></PrivateRoute>} />
                                <Route path="/missiontoolkit" element={<PrivateRoute><MissionToolkit /></PrivateRoute>} />
                                
                                {/* ===== FAMILY ROUTES ===== */}
                                {/* My Family - for all users (view their own family) */}
                                <Route path="/family" element={<PrivateRoute><Family /></PrivateRoute>} />
                                
                                {/* Family Allocation - for admins only (manage all families) */}
                                <Route 
                                    path="/family-allocation" 
                                    element={
                                        <PrivateRoute requireAdmin={true}>
                                            <Family />
                                        </PrivateRoute>
                                    } 
                                />
                                {/* ===== END FAMILY ROUTES ===== */}
                                
                                <Route path="/super-admin" element={<PrivateRoute requireAdmin={true}><SuperAdmin /></PrivateRoute>}/>
                                <Route path="/hymns" element={<PrivateRoute><Hymns /></PrivateRoute>} />
                                
                                {/* Wallet Routes */}
                                <Route path="/my-wallet" element={<PrivateRoute><UserWallet /></PrivateRoute>} />
                                
                                {/* Finance Route */}
                                <Route path="/finance" element={<PrivateRoute><Finance /></PrivateRoute>} />

                                {/* Admin Only Routes */}
                                <Route 
                                    path="/mission-toolkit" 
                                    element={
                                        <PrivateRoute requireAdmin={true}>
                                            <MissionToolkit />
                                        </PrivateRoute>
                                    } 
                                />
                                <Route 
                                    path="/letter" 
                                    element={
                                        <PrivateRoute requireAdmin={true}>
                                            <Letter />
                                        </PrivateRoute>
                                    } 
                                />
                                <Route 
                                    path="/admin-wallet" 
                                    element={
                                        <PrivateRoute requireAdmin={true}>
                                            <AdminWallet />
                                        </PrivateRoute>
                                    } 
                                />
                                <Route 
                                    path="/communication" 
                                    element={
                                        <PrivateRoute requireAdmin={true}>
                                            <Communication />
                                        </PrivateRoute>
                                    } 
                                />
                                <Route 
                                    path="/smscommunication" 
                                    element={
                                        <PrivateRoute requireAdmin={true}>
                                            <SMSCommunication />
                                        </PrivateRoute>
                                    } 
                                />
                                <Route 
                                    path="/reports" 
                                    element={
                                        <PrivateRoute requireAdmin={true}>
                                            <div>Reports Page (Coming Soon)</div>
                                        </PrivateRoute>
                                    } 
                                />

                                {/* Catch all */}
                                <Route path="*" element={<Navigate to="/" />} />
                            </Routes>
                         </AuthGate>
                        </Layout>

                        {/* Global Components */}
                        <Toast />
                        <AddYearModal 
                            isOpen={addYearModalOpen} 
                            onClose={() => setAddYearModalOpen(false)} 
                        />
                        <NotificationModal 
                            isOpen={notificationModalOpen} 
                            onClose={() => setNotificationModalOpen(false)} 
                        />
                    </WalletProvider>
                </ToastProvider>
            </AuthProvider>
        </Router>
 
    );
}

export default App;
